import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoMarketplaceRpcClientContext } from "@redotech/redo-marketplace-app-common/rpc-client";
import { SetVendorAuthContext } from "@redotech/redo-marketplace-app-common/vendor-auth";
import { memo, useContext, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export const Verify = memo(function Verify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const next = searchParams.get("next");

  const marketplaceRpcClient = useRequiredContext(
    RedoMarketplaceRpcClientContext,
  );
  const setVendorAuth = useContext(SetVendorAuthContext);

  const verifyResult = useLoad(async () => {
    if (!token) {
      return null;
    }
    return await marketplaceRpcClient.verifyMagicLink({ token });
  }, [token, marketplaceRpcClient]);

  useEffect(() => {
    if (verifyResult.value && setVendorAuth) {
      // Parse the returned auth token and set the vendor auth
      const { authToken, vendor } = verifyResult.value;

      // The auth token is a JWT that contains vendorId and email
      // We'll let the VendorAuthProvider parse it
      setVendorAuth({
        vendorId: vendor.id,
        email: vendor.email,
        token: authToken,
        expiration: Temporal.Instant.fromEpochMilliseconds(
          // JWT tokens are typically valid for 30 days as configured
          Temporal.Now.instant().epochMilliseconds + 30 * 24 * 60 * 60 * 1000,
        ),
      });

      // Redirect to the intended page or home
      const redirectTo = next ? decodeURIComponent(next) : "/";
      navigate(redirectTo, { replace: true });
    }
  }, [verifyResult.value, setVendorAuth, navigate, next]);

  if (!token) {
    return (
      <div
        style={{
          maxWidth: "400px",
          margin: "100px auto",
          padding: "var(--redo-spacing-2xl)",
        }}
      >
        <h1>Invalid Link</h1>
        <p>
          This magic link is missing a token. Please check your email and click
          the correct link.
        </p>
        <button
          onClick={() => navigate("/login")}
          style={{
            padding: "var(--redo-spacing-lg) var(--redo-spacing-3xl)",
            backgroundColor: "var(--redo-colors-blue-700)",
            color: "var(--redo-colors-text-text-white)",
            border: "none",
            borderRadius: "var(--border-radius-sm)",
            cursor: "pointer",
          }}
          type="button"
        >
          Go to login
        </button>
      </div>
    );
  }

  if (verifyResult.pending) {
    return (
      <div
        style={{
          maxWidth: "400px",
          margin: "100px auto",
          padding: "var(--redo-spacing-2xl)",
          textAlign: "center",
        }}
      >
        <h1>Signing you in...</h1>
        <p>Please wait while we verify your magic link.</p>
        <div
          style={{ margin: "var(--redo-spacing-2xl) var(--redo-spacing-none)" }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #0066cc",
              borderRadius: "var(--border-radius-full)",
              animation: "spin 1s linear infinite",
              margin: "var(--redo-spacing-none) auto",
            }}
          />
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  if (verifyResult.error) {
    return (
      <div
        style={{
          maxWidth: "400px",
          margin: "100px auto",
          padding: "var(--redo-spacing-2xl)",
        }}
      >
        <h1>Link Expired or Invalid</h1>
        <p>
          This magic link has expired or is invalid. Magic links are only valid
          for 1 hour after they're sent.
        </p>
        <div
          style={{
            marginTop: "var(--redo-spacing-xl)",
            padding: "var(--redo-spacing-lg)",
            backgroundColor: "var(--redo-colors-background-bg-error-primary)",
            border: "1px solid #fcc",
            borderRadius: "var(--border-radius-sm)",
            color: "var(--redo-colors-text-text-error-primary)",
          }}
        >
          {verifyResult.error.message || "Unable to verify your magic link."}
        </div>
        <button
          onClick={() => navigate("/login")}
          style={{
            marginTop: "var(--redo-spacing-xl)",
            padding: "var(--redo-spacing-lg) var(--redo-spacing-3xl)",
            backgroundColor: "var(--redo-colors-blue-700)",
            color: "var(--redo-colors-text-text-white)",
            border: "none",
            borderRadius: "var(--border-radius-sm)",
            cursor: "pointer",
          }}
          type="button"
        >
          Request a new magic link
        </button>
      </div>
    );
  }

  // If we get here, verification succeeded and we're redirecting
  return (
    <div
      style={{
        maxWidth: "400px",
        margin: "100px auto",
        padding: "var(--redo-spacing-2xl)",
        textAlign: "center",
      }}
    >
      <h1>Success!</h1>
      <p>You've been signed in successfully. Redirecting...</p>
    </div>
  );
});
